class VariantGalleryFilter {
  constructor(section) {
    this.section = section;

    this.mapEl = section.querySelector('[id^="VariantGalleryMap-"]');
    if (!this.mapEl) return;

    try {
      this.variantTokensById = JSON.parse(this.mapEl.textContent);
    } catch (e) {
      console.error('VariantGalleryMap JSON parse failed', e);
      return;
    }

    this.form = section.querySelector('form[action^="/cart/add"]');
    if (!this.form) return;

    // All gallery + thumbnail elements that we tagged
    this.tokenEls = Array.from(section.querySelectorAll('[data-media-token]'));

    // CONFIG: option index for color (0 = option1, 1 = option2, etc.)
    // In your JSON: option1 = color, option2 = size
    this.COLOR_OPTION_INDEX = 0;

    // Debug via ?vgdebug=1
    this.debug = new URLSearchParams(window.location.search).get('vgdebug') === '1';

    // Variant data (we read it from Dawn JSON scripts if present)
    this.variants = this.getVariantsData();

    // Precompute color -> tokens map by scanning variants that have metafield tokens
    this.colorTokensMap = this.buildColorTokensMap();

    this.onChange = this.onChange.bind(this);
    this.onVariantChange = this.onVariantChange.bind(this);

    this.form.addEventListener('change', this.onChange);

    // Dawn/Shopify themes often dispatch this custom event when variants change
    document.addEventListener('variant:change', this.onVariantChange);

    this.apply();
  }

  normalize(val) {
    return (val || '').toString().trim().toLowerCase();
  }

  getVariantsData() {
    // Dawn standard: variants JSON often lives in a script like #ProductVariants-<sectionId>
    const candidates = [
      this.section.querySelector('script[type="application/json"][id^="ProductVariants-"]'),
      this.section.querySelector('script[type="application/json"][id^="Variants-"]'),
      this.section.querySelector('script[type="application/json"][data-variants-json]'),
      this.section.querySelector('script[type="application/json"][data-variant-json]'),
      this.section.querySelector('script[type="application/json"][id^="ProductJSON-"]'),
      this.section.querySelector('script[type="application/json"][data-product-json]'),
      this.section.querySelector('script[type="application/json"][id^="ProductJson-"]'),
    ].filter(Boolean);

    for (const el of candidates) {
      try {
        const data = JSON.parse(el.textContent);
        if (Array.isArray(data)) return data; // sometimes directly variants array
        if (Array.isArray(data?.variants)) return data.variants;
        if (Array.isArray(data?.product?.variants)) return data.product.variants;
      } catch (e) {
        // try next
      }
    }

    // Global fallback (may be absent)
    const globalVariants = window.Shopify?.product?.variants;
    if (Array.isArray(globalVariants)) return globalVariants;

    return [];
  }

  getVariantIdFromForm() {
    const idEl = this.form.querySelector('input[name="id"], select[name="id"]');
    return idEl ? idEl.value : null;
  }

  getVariantIdFromUrl() {
    const v = new URLSearchParams(window.location.search).get('variant');
    return v ? v : null;
  }

  getSelectedOptionsFromDom() {
    // Supports Dawn <variant-radios> and <variant-selects>
    const opts = [];

    const radioFieldsets = this.section.querySelectorAll('variant-radios fieldset');
    if (radioFieldsets.length) {
      radioFieldsets.forEach((fs) => {
        const checked = fs.querySelector('input[type="radio"]:checked');
        if (checked) opts.push(checked.value);
      });
    }

    const selects = this.section.querySelectorAll('variant-selects select');
    if (selects.length) {
      selects.forEach((sel) => {
        if (sel.value) opts.push(sel.value);
      });
    }

    return opts;
  }

  findVariantBySelectedOptions() {
    const selected = this.getSelectedOptionsFromDom();
    if (!selected.length) return null;

    const normalizedSelected = selected.map((v) => this.normalize(v));

    return (
      this.variants.find((v) => {
        const opts = (v.options || []).map((o) => this.normalize(o));
        if (opts.length < normalizedSelected.length) return false;
        for (let i = 0; i < normalizedSelected.length; i++) {
          if (opts[i] !== normalizedSelected[i]) return false;
        }
        return true;
      }) || null
    );
  }

  getCurrentVariant() {
    // 1) form input/select
    const idFromForm = this.getVariantIdFromForm();
    if (idFromForm) {
      const v = this.variants.find((vv) => String(vv.id) === String(idFromForm));
      if (v) return v;
    }

    // 2) URL param ?variant=
    const idFromUrl = this.getVariantIdFromUrl();
    if (idFromUrl) {
      const v = this.variants.find((vv) => String(vv.id) === String(idFromUrl));
      if (v) return v;
    }

    // 3) match selected option values
    return this.findVariantBySelectedOptions();
  }

  getColorValue(variant) {
    // Prefer options array (present in your variants JSON)
    const raw = variant?.options?.[this.COLOR_OPTION_INDEX];
    return this.normalize(raw);
  }

  buildColorTokensMap() {
    const map = new Map();

    for (const variantId in this.variantTokensById) {
      const tokens = this.variantTokensById[variantId];
      if (!Array.isArray(tokens) || tokens.length === 0) continue;

      const variant = this.variants.find((v) => String(v.id) === String(variantId));
      if (!variant) continue;

      const color = this.getColorValue(variant);
      if (!color) continue;

      // First one wins per color
      if (!map.has(color)) map.set(color, tokens);
    }

    return map;
  }

  apply() {
    const variant = this.getCurrentVariant();

    if (!variant) {
      if (this.debug) {
        console.warn('[VG] No current variant found', {
          variantsLoaded: this.variants.length,
          idFromForm: this.getVariantIdFromForm(),
          idFromUrl: this.getVariantIdFromUrl(),
          selectedOptions: this.getSelectedOptionsFromDom(),
        });
      }
      return;
    }

    const color = this.getColorValue(variant);
    const allowedTokens = this.colorTokensMap.get(color) || [];

    const useFilter = Array.isArray(allowedTokens) && allowedTokens.length > 0;
    const normalizedAllowed = allowedTokens.map((t) => this.normalize(t));

    if (this.debug) {
      console.log('[VG] variant', variant.id, 'color', color, 'useFilter', useFilter, 'tokens', allowedTokens);
    }

    this.tokenEls.forEach((el) => {
      const token = this.normalize(el.getAttribute('data-media-token'));
      const visible = !useFilter || normalizedAllowed.includes(token);
      el.style.display = visible ? '' : 'none';
    });

    const viewer = this.section.querySelector('[id^="Slider-Gallery-"]');
    if (viewer) {
      const mediaItems = Array.from(viewer.querySelectorAll('li[data-media-id]'));
      const visibleItems = mediaItems.filter((item) => item.style.display !== 'none');
      const active = viewer.querySelector('.product__media-item.is-active');

      if (visibleItems.length && (!active || active.style.display === 'none')) {
        mediaItems.forEach((item) => item.classList.remove('is-active'));
        visibleItems[0].classList.add('is-active');
      }
    }
  }

  onChange() {
    requestAnimationFrame(() => this.apply());
  }

  onVariantChange(evt) {
    // Many Dawn setups dispatch variant:change globally; re-apply.
    // If the event contains a sectionId, we could scope it, but re-applying is cheap.
    requestAnimationFrame(() => this.apply());
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.shopify-section').forEach((section) => {
    if (section.querySelector('[id^="VariantGalleryMap-"]')) {
      new VariantGalleryFilter(section);
    }
  });
});
